import { useMemo, useState } from "react";

import { usePagination } from "@vega-api/custom/utils/usePagination";
import { useFeatureFlag } from "@vega-api/extended/auth/useFeatureFlag";
import {
  useGetFundCasSettingsByFilters,
  useGetFundCasTemplates,
} from "@vega-api/generated/cas-settings-v1/cas-settings-v1";
import { useGetFunds } from "@vega-api/generated/fund-management-v1/fund-management-v1";
import { DefaultErrorPanel } from "@vega-core/components/Cards/DefaultErrorPanel/DefaultErrorPanel";
import { CoreTable } from "@vega-core/components/Table/CoreTable/CoreTable";
import { useBoolean } from "@vega-core/hooks/useBoolean";
import { usePaginationState } from "@vega-core/hooks/usePaginationState";
import { type OptionType } from "@vega-core/models/options";
import { m } from "@vega-core/paraglide/messages";
import { EditCASVisibilityModal } from "@vega-modules/scenes/Funds/FundDetails/CAS/EditCASVisibilityModal";

import {
  type FundCASTemplate,
  useCASTableColumns,
} from "../hooks/useCASTableColumns";
import { useCASTableFilters } from "../hooks/useCASTableFilters";

export const CASManagement = () => {
  const [selectedFund, setSelectedFund] = useState<FundCASTemplate | null>(
    null,
  );
  const editModalState = useBoolean(false);
  const paginationState = usePaginationState();
  const { featureFlag } = useFeatureFlag();
  const isV2 = featureFlag.fund_management_v2_endpoints;

  const { data: fundsData, isError: fundsError } = useGetFunds(undefined, {
    query: { enabled: !isV2 },
  });
  const { data: templatesData, isLoading: templatesLoading } =
    useGetFundCasTemplates();

  const templateOptions: OptionType[] = useMemo(
    () =>
      templatesData?.data?.map((template) => ({
        label: template.templateName,
        value: template.templateKey,
      })) || [],
    [templatesData],
  );

  const { filterState, RenderFilters } = useCASTableFilters({
    templateOptions,
    isLoading: templatesLoading,
  });

  const {
    data: casSettingsData,
    isLoading: isCasLoading,
    isError: casError,
  } = useGetFundCasSettingsByFilters(
    {
      page: paginationState.currentPage,
      pageSize: paginationState.currentPageSize,
      searchQuery: filterState.search,
      active: filterState.active
        ? filterState.active.value === "true"
        : undefined,
      templateKey: filterState.templateKey?.value || "",
    },
    {
      query: {
        enabled: true,
        refetchOnWindowFocus: false,
        staleTime: 30000,
      },
    },
  );

  const handleEditVisibility = (fund: FundCASTemplate) => {
    setSelectedFund(fund);
    editModalState.setTrue();
  };

  const handleModalClose = () => {
    setSelectedFund(null);
    editModalState.setFalse();
  };

  const columns = useCASTableColumns({
    onEditVisibility: handleEditVisibility,
  });

  const fundLookup = useMemo(
    () =>
      Object.fromEntries(fundsData?.data?.map((fund) => [fund.id, fund]) || []),
    [fundsData],
  );

  const tableData: FundCASTemplate[] = useMemo(
    () =>
      casSettingsData?.data?.map((casSettings) => ({
        id: casSettings.fundId,
        fundName: casSettings.fundName,
        assetClass: isV2
          ? casSettings.assetClass
          : // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
            fundLookup[casSettings.fundId]?.attributes.assetClass,
        template: {
          templateKey: casSettings.templateKey,
          templateName: casSettings.templateName,
        },
        lpPortalVisibility: casSettings.isActive,
      })) || [],
    [casSettingsData, fundLookup, isV2],
  );

  const { paginationElement } = usePagination({
    meta: casSettingsData?.meta ?? null,
    currentPage: paginationState.currentPage,
    setCurrentPage: paginationState.setCurrentPage,
    isLoading: isCasLoading,
    currentPageSize: paginationState.currentPageSize,
    setCurrentPageSize: paginationState.setCurrentPageSize,
    filterDependencies: [
      filterState.search,
      filterState.templateKey,
      filterState.active,
    ],
  });

  if (casError) {
    return <DefaultErrorPanel />;
  }

  if (!isV2 && fundsError) {
    return <DefaultErrorPanel />;
  }

  return (
    <>
      {RenderFilters}

      <CoreTable
        columns={columns}
        data={tableData}
        dataTestId="cas-templates-table"
        emptyState={{
          title: m.settings_casManagement_table_emptyState_title(),
          description: m.settings_casManagement_table_emptyState_description(),
        }}
        isLoading={isCasLoading}
        paginationElement={paginationElement}
        tableClassName="w-full"
        wrapperClassName="w-full"
      />

      {selectedFund ? (
        <EditCASVisibilityModal
          assetClass={selectedFund.assetClass}
          fundId={selectedFund.id}
          fundName={selectedFund.fundName || ""}
          onOpenChange={handleModalClose}
          open={editModalState.value}
        />
      ) : null}
    </>
  );
};
